// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { FileText, Loader2, Mail, CheckCircle2, XCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { RecaptchaGuard, useCaptchaToken } from '@/app/features/auth/components/recaptcha-provider';
import { useAuthConfig } from '@/app/features/auth/hooks/useAuthConfig';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/app/stores/auth-store';

const RESEND_COOLDOWN_SECONDS = 180;
const RESEND_COOLDOWN_STORAGE_KEY = 'verifyEmailResendCooldownStartedAt';

function formatCooldown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Email verification page.
 *
 * Two modes:
 * 1. If a `token` query param is present, the page auto-verifies the email.
 * 2. Otherwise, it shows a "check your email" screen with a resend button.
 */
function VerifyEmailPageContent() {
  const router = useRouter();
  const { user, accessToken, setUser } = useAuthStore();
  const getCaptchaToken = useCaptchaToken();
  const { recaptchaEnabled, isLoading: isAuthConfigLoading } = useAuthConfig();
  const [status, setStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) return;

    const now = Date.now();
    const savedStart = Number(window.localStorage.getItem(RESEND_COOLDOWN_STORAGE_KEY) || '0');
    const cooldownEnd = savedStart + RESEND_COOLDOWN_SECONDS * 1000;

    if (savedStart > 0 && cooldownEnd > now) {
      setCooldownRemaining(Math.ceil((cooldownEnd - now) / 1000));
      return;
    }

    window.localStorage.setItem(RESEND_COOLDOWN_STORAGE_KEY, String(now));
    setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
  }, []);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const timer = window.setInterval(() => {
      setCooldownRemaining((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 0) {
          window.localStorage.removeItem(RESEND_COOLDOWN_STORAGE_KEY);
        }
        return next;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldownRemaining]);

  async function getCaptchaTokenWithRetry(action: string): Promise<string | undefined> {
    if (!recaptchaEnabled) return undefined;

    for (let i = 0; i < 12; i += 1) {
      const token = await getCaptchaToken(action);
      if (token) return token;
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }

    return undefined;
  }

  // Check for token in URL to auto-verify.
  useEffect(() => {
    if (isAuthConfigLoading) return;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return;

    const verifyEmail = async () => {
      setStatus('verifying');
      try {
        const captchaToken = await getCaptchaTokenWithRetry('verify_email');
        if (recaptchaEnabled && !captchaToken) {
          setStatus('error');
          setErrorMessage('reCAPTCHA is still initializing. Please try again in a moment.');
          return;
        }

        const { data, error } = await apiClient.POST('/api/v1/auth/verify-email', {
          body: { token, captcha_token: captchaToken },
        });

        if (error) {
          setStatus('error');
          setErrorMessage(error.error.message);
          return;
        }

        setStatus('success');
        if (data?.data) {
          const { access_token, refresh_token, user: userData } = data.data;
          useAuthStore.getState().setTokens(access_token, refresh_token);
          useAuthStore.getState().setUser({
            id: userData.id,
            email: userData.email,
            creditBalance: userData.credit_balance,
            aiProvider: userData.ai_provider,
            storageProvider: userData.storage_provider ?? undefined,
            hasPassword: userData.has_password ?? undefined,
            emailVerified: true,
          });
        } else if (user) {
          setUser({ ...user, emailVerified: true });
        }

        setTimeout(() => {
          router.navigate({ to: '/dashboard' });
        }, 2000);
      } catch {
        setStatus('error');
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    };

    void verifyEmail();
  }, [getCaptchaToken, isAuthConfigLoading, recaptchaEnabled]);

  async function handleResend() {
    setResending(true);
    setResendSuccess(false);
    try {
      const captchaToken = await getCaptchaTokenWithRetry('resend_verification');
      if (recaptchaEnabled && !captchaToken) {
        setErrorMessage('reCAPTCHA is still initializing. Please try again in a moment.');
        return;
      }

      const { error } = await apiClient.POST('/api/v1/auth/resend-verification', {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        body: { captcha_token: captchaToken } as never,
      });
      if (error) {
        setErrorMessage(error.error.message);
      } else {
        setResendSuccess(true);
        window.localStorage.setItem(RESEND_COOLDOWN_STORAGE_KEY, String(Date.now()));
        setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
      }
    } catch {
      setErrorMessage('Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="mb-8 flex items-center justify-center gap-2">
          <FileText className="h-8 w-8 text-primary" />
          <span className="text-[24px] font-semibold tracking-tight">FlairCV</span>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          {status === 'verifying' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[14px] text-muted-foreground">Verifying your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <h1 className="text-[18px] font-semibold text-card-foreground">
                Email verified
              </h1>
              <p className="text-center text-[13px] text-muted-foreground">
                Your email has been verified. Redirecting to your dashboard...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <XCircle className="h-10 w-10 text-destructive" />
              <h1 className="text-[18px] font-semibold text-card-foreground">
                Verification failed
              </h1>
              <p className="text-center text-[13px] text-muted-foreground">
                {errorMessage || 'The verification link is invalid or has expired.'}
              </p>
              <button
                onClick={handleResend}
                disabled={resending || cooldownRemaining > 0}
                className="mt-2 flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
              >
                {resending && <Loader2 className="h-4 w-4 animate-spin" />}
                Resend verification email
              </button>
              {cooldownRemaining > 0 && (
                <p className="text-[12px] text-muted-foreground">
                  Resend available in {formatCooldown(cooldownRemaining)}
                </p>
              )}
            </div>
          )}

          {status === 'pending' && (
            <>
              <div className="flex flex-col items-center gap-4 py-4">
                <Mail className="h-10 w-10 text-primary" />
                <h1 className="text-[18px] font-semibold text-card-foreground">
                  Check your email
                </h1>
                <p className="text-center text-[13px] text-muted-foreground">
                  We sent a verification link to{' '}
                  <strong className="text-foreground">{user?.email || 'your email'}</strong>.
                  Click the link to activate your account.
                </p>
              </div>

              {/* Divider */}
              <hr className="my-4 border-border" />

              <p className="text-center text-[12px] text-muted-foreground">
                Didn't receive the email? Check your spam folder or
              </p>

              {resendSuccess && (
                <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-center text-[13px] text-green-700">
                  Verification email sent. Check your inbox.
                </div>
              )}

              {errorMessage && !resendSuccess && (
                <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-center text-[13px] text-destructive">
                  {errorMessage}
                </div>
              )}

              <button
                onClick={handleResend}
                disabled={resending || cooldownRemaining > 0}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
              >
                {resending && <Loader2 className="h-4 w-4 animate-spin" />}
                Resend verification email
              </button>
              {cooldownRemaining > 0 && (
                <p className="mt-2 text-center text-[12px] text-muted-foreground">
                  Resend available in {formatCooldown(cooldownRemaining)}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function VerifyEmailPage() {
  return (
    <RecaptchaGuard>
      <VerifyEmailPageContent />
    </RecaptchaGuard>
  );
}
